import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../utils/Hooks";
import {Link, useLocation, useNavigate, useParams} from "react-router-dom";
import { LoggedInView } from "../common/LoggedInView";
import Breadcrumbs from "../common/Breadcrumbs";
import ElogSelect, { Option } from "../common/form/ElogSelect";
import { BreadcrumbLink } from "../../models/BreadcrumbLink";
import {
    createInquiry,
    getCurrentInquiry,
    resetUpdateInquiryStatus,
    updateInquiry,
} from "../../store/reducers/InquiriesSlice";
import { RequestStatus } from "../../utils/Enums";
import RouteInput from "./components/Routenput";
import ClientInput from "../common/form/ClientInput";
import ElogTextarea from "../common/form/ElogTextarea";
import WeightInput from "../common/form/WeightInput";
import {getCountries} from "../../store/reducers/CountriesSlice";
import {getServices} from "../../store/reducers/ServicesSlice";
import {getUsers} from "../../store/reducers/UsersSlice";
import ElogInput from "../common/form/ElogInput";
import {FormikProvider, useFormik, Form} from "formik";

interface ModifyInquiryScreenProps {}

export interface ModifyInquiryFormProps {
    user: number;
    expires_at: string;
}

export const ModifyInquiryScreen: React.FC<ModifyInquiryScreenProps> = () => {

    const {id} = useParams();

    const STORAGE_KEY = id ? "modifyInquiryFormData" + id : "";
    const saved = id ? sessionStorage.getItem(STORAGE_KEY) : null;
    const savedValues: any = saved ? (() => {
        try {
            const parsed = JSON.parse(saved);
            return Object.values(parsed).some(v => v !== "" && v !== false && v !== null) ? parsed : {};
        } catch {
            return {};
        }
    })() : {};

    const resetStorage = () => {
        if (STORAGE_KEY) sessionStorage.removeItem(STORAGE_KEY);
    };

    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    const inquiriesState = useAppSelector((state) => state.inquiries);
    const countriesState = useAppSelector((state) => state.countries);
    const servicesState = useAppSelector((state) => state.services);
    const usersState = useAppSelector((state) => state.users);

    const requestStatus: RequestStatus = inquiriesState.updateInquiryStatus;

    const breadcrumbLinks: BreadcrumbLink[] = [
        { text: "Päringud", url: "/inquiries" },
        { text: "Muuda päringut", url: `/inquiries/${id}/modify` },
    ];

    useEffect(() => {
        dispatch(resetUpdateInquiryStatus());
    }, [location]);

    useEffect(() => {
        if (id) {
            dispatch(getCurrentInquiry({ id: parseInt(id) }));
        }
    }, [id]);

    useEffect(() => {
        dispatch(getCountries());
        dispatch(getServices(false));
        dispatch(getUsers());
    }, []);

    const [hydrated, setHydrated] = useState(false);

    const formik = useFormik({
        initialValues: {
            startCountry: "",
            startCity: "",
            destinationCountry: "",
            destinationCity: "",
            service: "",
            comment: "",
            amount: "",
            weight: "",
            noWeight: false,
            client: "",
            clientContact: "",
            buySum: "",
            saleSum: "",
            user: "",
        },
        onSubmit: (values: any) => {
            dispatch(updateInquiry({ id, data: values }));
            resetStorage();
        },
        enableReinitialize: false
    });

    useEffect(() => {
        if (!id || !inquiriesState.currentInquiry || inquiriesState.currentInquiry.id !== parseInt(id)) return;
        if (hydrated) return;

        const nextValues: any = {
            startCountry: savedValues.startCountry ?? inquiriesState?.currentInquiry?.start_country_id ?? "",
            startCity: savedValues.startCity ?? inquiriesState?.currentInquiry?.start_city ?? "",
            destinationCountry: savedValues.destinationCountry ?? inquiriesState?.currentInquiry?.destination_country_id ?? "",
            destinationCity: savedValues.destinationCity ?? inquiriesState?.currentInquiry?.destination_city ?? "",
            service: savedValues.service ?? inquiriesState?.currentInquiry?.service_id ?? "",
            comment: savedValues.comment ?? inquiriesState?.currentInquiry?.comment ?? "",
            amount: savedValues.amount ?? inquiriesState?.currentInquiry?.amount ?? "",
            weight: savedValues.weight ?? inquiriesState?.currentInquiry?.weight ?? "",
            noWeight: savedValues.noWeight ?? (inquiriesState?.currentInquiry?.no_weight === 1),
            client: savedValues.client ?? inquiriesState?.currentInquiry?.client?.name ?? "",
            clientContact: savedValues.clientContact ?? inquiriesState?.currentInquiry?.client_contact_id ?? "",
            buySum: savedValues.buySum ?? inquiriesState?.currentInquiry?.buy_sum ?? "",
            saleSum: savedValues.saleSum ?? inquiriesState?.currentInquiry?.sale_sum ?? "",
            user: savedValues.user ?? inquiriesState?.currentInquiry?.user_id ?? "",
        };

        formik.setValues(nextValues);
        setHydrated(true);
    }, [id, inquiriesState.currentInquiry, hydrated]);

    useEffect(() => {
        if (!id || !inquiriesState.currentInquiry || inquiriesState.currentInquiry.id !== parseInt(id)) return;
        if (!hydrated) return;
        if (requestStatus === RequestStatus.Success) return;
        if (!Object.values(formik.values).some(value => value !== "" && value !== false && value !== null)) return;
        const parsed = { ...formik.values };
        sessionStorage.setItem("modifyInquiryFormData" + id, JSON.stringify(parsed));
    }, [formik.values, inquiriesState.currentInquiry, id, hydrated, requestStatus]);

    useEffect(() => {
        if (requestStatus === RequestStatus.Success) {
            resetStorage();
        }
    }, [requestStatus]);

    const countryOptions: Option[] = countriesState.countries.map((country) => ({
        value: country.id.toString(),
        label: country.name,
    }));

    const serviceOptions: Option[] = servicesState.services.map((service) => ({
        value: service.id.toString(),
        label: service.name,
    }));

    const userOptions: Option[] = [
        ...usersState.users
            .filter((user) => user.logistician === 1 || user.is_checker === 1)
            .map(user => ({
                value: user.id.toString(),
                label: `${user.firstname} ${user.lastname}`,
            }))
    ];

    const handleCancel = () => {
        resetStorage();
        navigate(-1);
    };

    const {values, setFieldValue, handleSubmit} = formik;

    return (
        <LoggedInView fullWidth={false}>
            <Breadcrumbs links={breadcrumbLinks} />
            {requestStatus === RequestStatus.Success && (
                <div className="message msg-ok">
                    <div>
                        <h2>Andmed muudetud</h2>
                    </div>
                </div>
            )}
            {requestStatus === RequestStatus.Failed &&
            inquiriesState.errors.length > 0 && (
                <div className="message msg-error">
                    <div>
                        <h2>Viga:</h2>
                        {inquiriesState.errors.map((error, index) => (
                            <span key={index}>{error}<br /></span>
                        ))}
                    </div>
                </div>
            )}
            <FormikProvider value={formik}>
                <Form onSubmit={handleSubmit}>
                    <div className="form">
                        <h2>
                            <span>Päringu andmed</span>
                        </h2>
                        <table className="form">
                            <tbody>
                            <ElogSelect label="Logistik" name="user" options={userOptions} mandatory={true}/>
                            <RouteInput countries={countryOptions} />
                            <ClientInput />
                            <ElogSelect label="Teenuse liik" name="service" options={serviceOptions} mandatory={true}/>
                            <ElogTextarea label="Kommentaar" name="comment" mandatory={false}/>
                            <ElogTextarea label="Kaubakogus" name="amount" mandatory={true}/>
                            <WeightInput />
                            {inquiriesState?.currentInquiry?.state === 'answered' &&
                            <>
                              <ElogInput label="Ostu summa" name="buySum" />
                              <ElogInput label="Müügi summa" name="saleSum" />
                            </>
                            }
                            </tbody>
                        </table>
                    </div>
                    <div className="action clear">
                        <p>
                            <button type="submit">
                  <span>
                    <strong>Kinnita</strong>
                  </span>
                            </button>
                            <Link to="" onClick={handleCancel}>Tühista</Link>
                        </p>
                    </div>
                </Form>
            </FormikProvider>
        </LoggedInView>
    )

}